import isEmail from "validator/lib/isEmail"

/**
 * Form validation utilities
 */

/**
 * Validates an email address
 * @param email The email to validate
 * @returns Undefined if valid, error message if invalid
 */
export const validateEmail = (email: string): string | undefined => {
  if (!email) {
    return "Email is required"
  }

  // Simple regex for email validation
  if (!isEmail(email)) {
    return "Please enter a valid email address"
  }

  return undefined
}

/**
 * Validates a 6-digit verification code
 */
export const validateVerificationCode = (code: string): string | undefined => {
  if (!code) {
    return "Verification code is required"
  }

  if (!/^\d{6}$/.test(code)) {
    return "Verification code must be 6 digits"
  }

  return undefined
}

/**
 * Validates a password
 */
export const validatePassword = (password: string): string | undefined => {
  if (!password) {
    return "Password is required"
  }

  if (password.length < 8) {
    return "Password must be at least 8 characters"
  }

  return undefined
}

/**
 * Validates that password confirmation matches the password
 */
export const validatePasswordConfirmation = (
  confirmation: string,
  { password }: { password: string },
): string | undefined => {
  if (!confirmation) {
    return "Please confirm your password"
  }

  if (confirmation !== password) {
    return "Passwords don't match"
  }

  return undefined
}

/**
 * Validates a master key
 */
export const validateMasterKey = (masterKey: string): string | undefined => {
  if (!masterKey) {
    return "Master key is required"
  }

  // Master key format is XXXX-XXXX-XXXX-XXXX
  const regex = /^[A-Z]{4}-[A-Z]{4}-[A-Z]{4}-[A-Z]{4}$/
  if (!regex.test(masterKey)) {
    return "Invalid master key format. It should be in the format XXXX-XXXX-XXXX-XXXX"
  }

  return undefined
}

/**
 * Validates a task title
 */
export const validateTaskTitle = (title: string): string | undefined => {
  if (!title.trim()) {
    return "Title is required"
  }

  return undefined
}

/**
 * Validates email uniqueness for signup (async validation)
 * @param email The email to check
 * @param checkEmailFn Function to check if email exists
 * @returns Promise with error message if email exists, undefined if available
 */
export const validateEmailUniqueness = async (
  email: string,
  checkEmailFn: (email: string) => Promise<boolean>,
): Promise<string | undefined> => {
  if (!email || validateEmail(email)) {
    // Skip async validation if email is empty or fails basic validation
    return undefined
  }

  try {
    const emailExists = await checkEmailFn(email)
    if (emailExists) {
      return "This email is already registered. Please use a different email or try logging in."
    }
    return undefined
  } catch (error) {
    console.error("Email uniqueness check failed:", error)
    // Don't block signup if email check fails - let the server handle it
    return undefined
  }
}
